import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { customersAPI, poLocationsAPI } from "../../../api/client";
import Toast from "../components/Toast";
import "./CreatePO.css";

const inputStyle = (hasError) => ({
  width: "100%", padding: "8px 12px",
  border: `1px solid ${hasError ? "#fca5a5" : "#d6dfed"}`,
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
  background: hasError ? "#fff5f5" : "#fff",
});
const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 };
const errStyle = { fontSize: 12, color: "#dc2626", marginTop: 3 };

const API_UPLOAD_URL = process.env.REACT_APP_API_UPLOAD_URL || "";

function PartSelect({ value, onChange, parts, hasError }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = parts.find((p) => p.id === value) || null;

  const filtered = parts.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
  });

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", padding: "7px 12px",
          border: `1px solid ${hasError ? "#fca5a5" : "#d6dfed"}`,
          borderRadius: 8, fontSize: 14, boxSizing: "border-box",
          background: hasError ? "#fff5f5" : "#fff",
          cursor: "pointer", textAlign: "left",
          display: "flex", alignItems: "center", gap: 8,
        }}
      >
        {selected ? (
          <>
            {selected.imageFileName ? (
              <img
                src={`${API_UPLOAD_URL}${selected.imageFileName}`}
                alt={selected.name}
                style={{ width: 26, height: 26, objectFit: "cover", borderRadius: 4, border: "1px solid #e4ebf5", flexShrink: 0 }}
              />
            ) : (
              <span style={{ width: 26, height: 26, borderRadius: 4, background: "#eff6ff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>⚙️</span>
            )}
            <span style={{ flex: 1, color: "#0f2d5c", fontWeight: 500 }}>{selected.name}</span>
            <span style={{ color: "#94a3b8", fontSize: 12, fontFamily: "monospace" }}>{selected.code}</span>
          </>
        ) : (
          <span style={{ color: "#94a3b8" }}>— Select part —</span>
        )}
        <span style={{ marginLeft: "auto", color: "#94a3b8", fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1px solid #d6dfed", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search parts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "100%", padding: "6px 10px", border: "1px solid #e4ebf5", borderRadius: 6, fontSize: 13, boxSizing: "border-box", outline: "none" }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px 14px", fontSize: 13, color: "#94a3b8" }}>No parts match</div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  style={{
                    width: "100%", padding: "8px 12px", border: "none",
                    background: p.id === value ? "#eff6ff" : "transparent",
                    cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                  }}
                  onMouseEnter={(e) => { if (p.id !== value) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = p.id === value ? "#eff6ff" : "transparent"; }}
                >
                  {p.imageFileName ? (
                    <img
                      src={`${API_UPLOAD_URL}${p.imageFileName}`}
                      alt={p.name}
                      style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: "1px solid #e4ebf5", flexShrink: 0 }}
                    />
                  ) : (
                    <span style={{ width: 32, height: 32, borderRadius: 6, background: "#eff6ff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>⚙️</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#0f2d5c", fontSize: 13 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{p.code}</div>
                  </div>
                  {p.id === value && <span style={{ color: "#1d4ed8", fontSize: 14 }}>✓</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Searchable dropdown backed by a master list, with inline "add new" — used for
// both the customer and the location field.
function AddableSelect({ value, onChange, items, setItems, createItem, noun, placeholder, hasError }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = items.find((c) => c.id === value) || null;

  const filtered = items.filter((c) => {
    const q = query.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q);
  });

  const showAdd =
    query.trim().length > 0 &&
    !items.some((c) => c.name.toLowerCase() === query.trim().toLowerCase());

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleFocus = () => {
    setQuery("");
    setOpen(true);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    if (value) onChange("");
    setOpen(true);
  };

  const handleSelect = (item) => {
    onChange(item.id);
    setQuery(item.name);
    setOpen(false);
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    setCreateError("");
    try {
      const created = await createItem(name);
      setItems((prev) => [...prev, created]);
      onChange(created.id);
      setQuery(created.name);
      setOpen(false);
    } catch (err) {
      setCreateError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        `Failed to create ${noun}`
      );
    } finally {
      setCreating(false);
    }
  };

  const displayValue = open ? query : (selected ? selected.name : query);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        style={inputStyle(hasError)}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1px solid #d6dfed", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
        }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 && !showAdd && (
              <div style={{ padding: "10px 14px", fontSize: 13, color: "#94a3b8" }}>No {noun}s found</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(c)}
                style={{
                  width: "100%", padding: "9px 14px", border: "none",
                  background: c.id === value ? "#eff6ff" : "transparent",
                  cursor: "pointer", textAlign: "left", fontSize: 13,
                  color: c.id === value ? "#1d4ed8" : "#0f2d5c", fontWeight: c.id === value ? 600 : 400,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
                onMouseEnter={(e) => { if (c.id !== value) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = c.id === value ? "#eff6ff" : "transparent"; }}
              >
                {c.name}
                {c.id === value && <span style={{ fontSize: 12, color: "#1d4ed8" }}>✓</span>}
              </button>
            ))}
            {showAdd && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCreate}
                disabled={creating}
                style={{
                  width: "100%", padding: "9px 14px", border: "none",
                  borderTop: filtered.length > 0 ? "1px solid #f1f5f9" : "none",
                  background: "transparent", cursor: "pointer", textAlign: "left",
                  fontSize: 13, color: "#1d4ed8", fontWeight: 600,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {creating ? "Adding…" : `+ Add "${query.trim()}" as new ${noun}`}
              </button>
            )}
            {createError && (
              <div style={{ padding: "6px 14px", fontSize: 12, color: "#dc2626" }}>{createError}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StageSkipSelect({ workflow, skippedStageIds, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!workflow) {
    return <span style={{ fontSize: 12, color: "#cbd5e1" }}>— Select workflow first —</span>;
  }

  const stages = workflow.stages || [];
  const activeCount = stages.length - skippedStageIds.length;

  const toggleStage = (stageId) => {
    const isSkipped = skippedStageIds.includes(stageId);
    if (!isSkipped && activeCount <= 1) return; // keep at least one active stage
    onChange(isSkipped ? skippedStageIds.filter((id) => id !== stageId) : [...skippedStageIds, stageId]);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", padding: "7px 12px", border: "1px solid #d6dfed",
          borderRadius: 8, fontSize: 13, boxSizing: "border-box",
          background: "#fff", cursor: "pointer", textAlign: "left",
          display: "flex", alignItems: "center", gap: 6,
        }}
      >
        <span style={{ color: activeCount < stages.length ? "#c2410c" : "#374151", flex: 1 }}>
          {activeCount} of {stages.length} stage{stages.length !== 1 ? "s" : ""}
        </span>
        <span style={{ color: "#94a3b8", fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1px solid #d6dfed", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", minWidth: 220,
        }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {stages.map((s) => {
              const checked = !skippedStageIds.includes(s.id);
              return (
                <label
                  key={s.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                    cursor: "pointer", fontSize: 13, color: checked ? "#0f2d5c" : "#94a3b8",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleStage(s.id)} />
                  {s.name}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const EXTRACT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

const emptyPartRow = () => ({ _key: String(Date.now() + Math.random()), partId: "", quantity: "", workflowId: "", deliveryDate: "", skippedStageIds: [] });
const isBlankRow = (p) => !p.partId && !p.quantity && !p.workflowId && !p.deliveryDate;

function ExtractHint({ warn, children }) {
  return <p style={{ fontSize: 12, marginTop: 3, color: warn ? "#b45309" : "#1d4ed8" }}>{children}</p>;
}

const EXTRACT_STEPS = [
  "Reading the document…",
  "Finding the PO number and customer…",
  "Picking out line items…",
  "Reading quantities and delivery dates…",
  "Matching against your parts and customers…",
];

// Blocks the whole screen while the PO is being read so the user can't
// navigate away mid-request; Cancel is the only way out.
function POExtractOverlay({ fileName, onCancel }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep((s) => Math.min(s + 1, EXTRACT_STEPS.length - 1)), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="po-extract-overlay" role="alertdialog" aria-modal="true" aria-labelledby="po-extract-title" aria-describedby="po-extract-status">
      <div className="po-extract-card">
        <div className="po-extract-doc" aria-hidden="true">
          <div className="po-extract-line" />
          <div className="po-extract-line" />
          <div className="po-extract-line" />
          <div className="po-extract-line" />
          <div className="po-extract-line" />
          <div className="po-extract-line" />
          <div className="po-extract-beam" />
        </div>
        <h3 id="po-extract-title" className="po-extract-title">Reading your PO</h3>
        <p id="po-extract-status" key={step} className="po-extract-status" aria-live="polite">{EXTRACT_STEPS[step]}</p>
        <div className="po-extract-dots" aria-hidden="true"><span /><span /><span /></div>
        <p className="po-extract-hint">
          {fileName ? <><strong>{fileName}</strong> · </> : null}This usually takes 10–30 seconds. Please stay on this page.
        </p>
        <button type="button" className="po-extract-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Upload a customer's PO (photo, scan or PDF) to pre-fill the form below
function POUploadPanel({ extracting, extraction, onFile }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = (file) => { if (file && !extracting) onFile(file); };

  const attention = extraction
    ? extraction.lines.filter((l) => !l.partId || !l.quantity || !l.deliveryDate || l.warnings.length).length
      + (extraction.customer.matchedId ? 0 : 1)
    : 0;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
      style={{
        border: `1.5px dashed ${dragOver ? "#1d4ed8" : "#bfd0ea"}`, borderRadius: 10,
        background: dragOver ? "#eff6ff" : "#f8fafc", padding: "14px 16px", marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f2d5c" }}>Auto-fill from a PO document</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Drop the customer's PO here, or upload a photo, scan or PDF (max 10 MB).
          </div>
        </div>
        <button type="button" className="btn-secondary" style={{ fontSize: 13 }} disabled={extracting} onClick={() => inputRef.current?.click()}>
          {extracting ? "Reading PO…" : extraction ? "Upload another" : "Upload PO"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={EXTRACT_ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>

      {extraction && (
        <div style={{ marginTop: 12, fontSize: 13, color: "#334155", background: "#fff", border: "1px solid #e4ebf5", borderRadius: 8, padding: "10px 12px" }}>
          Filled from <strong>{extraction.fileName}</strong>: {extraction.lines.length} line item{extraction.lines.length !== 1 ? "s" : ""}
          {attention > 0 ? <>, <strong style={{ color: "#b45309" }}>{attention} need{attention === 1 ? "s" : ""} attention</strong></> : null}.
          {" "}Check every field against the document before creating the PO. Workflows must be selected by hand.
          {extraction.warnings.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: "#b45309" }}>
              {extraction.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreatePO() {
  const navigate = useNavigate();
  const [poNumber, setPoNumber] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  const [parts, setParts] = useState([{ _key: "0", partId: "", quantity: "", workflowId: "", deliveryDate: "", skippedStageIds: [] }]);
  const [workflows, setWorkflows] = useState([]);
  const [partsMaster, setPartsMaster] = useState([]);
  // Feasibility is per part now — keyed by row _key
  const [feasibility, setFeasibility] = useState({});
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [extracting, setExtracting] = useState(null); // file name while a PO is being read
  const extractAbortRef = useRef(null);
  const [extraction, setExtraction] = useState(null);
  const debounceRef = useRef(null);

  // Leaving the page (browser back, sidebar) cancels an in-flight PO read;
  // refresh / tab close gets the browser's "leave site?" prompt.
  useEffect(() => () => extractAbortRef.current?.abort(), []);
  useEffect(() => {
    if (!extracting) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [extracting]);

  useEffect(() => {
    api.get("/manufacturing/workflows")
      .then((r) => {
        const active = (r.data.data || []).filter((w) => w.status === "active");
        setWorkflows(active);
      })
      .catch(() => {});

    api.get("/manufacturing/parts")
      .then((r) => setPartsMaster(r.data.data || []))
      .catch(() => {});

    customersAPI.getAll()
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});

    poLocationsAPI.getAll()
      .then((r) => setLocations(r.data.data || []))
      .catch(() => {});
  }, []);

  const totalQty = parts.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

  const validFeasibilityParts = parts.filter(
    (p) => p.partId && Number(p.quantity) > 0 && p.workflowId && p.deliveryDate
  );

  useEffect(() => {
    if (validFeasibilityParts.length === 0) {
      setFeasibility({});
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setFeasibilityLoading(true);
      try {
        const results = await Promise.all(
          validFeasibilityParts.map((p) =>
            api.post("/manufacturing/pos/check-feasibility", {
              workflowId: p.workflowId,
              quantity: Number(p.quantity),
              deliveryDate: p.deliveryDate,
              parts: [{ partId: p.partId, quantity: Number(p.quantity) }],
            })
          )
        );
        setFeasibility(
          Object.fromEntries(
            validFeasibilityParts.map((p, i) => [p._key, results[i].data.data])
          )
        );
      } catch {
        setFeasibility({});
      } finally {
        setFeasibilityLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [JSON.stringify(validFeasibilityParts.map((p) => ({ k: p._key, partId: p.partId, quantity: p.quantity, workflowId: p.workflowId, deliveryDate: p.deliveryDate })))]); // eslint-disable-line

  const addPart = () => setParts((prev) => [...prev, emptyPartRow()]);

  // Only fills the form — nothing is created until the user reviews and submits
  const handleExtractFile = async (file) => {
    const controller = new AbortController();
    extractAbortRef.current = controller;
    setExtracting(file.name);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/manufacturing/pos/extract", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
      });
      const data = res.data.data;

      if (data.poNumber) setPoNumber(data.poNumber);
      if (data.customer.matchedId) setCustomerId(data.customer.matchedId);
      if (data.location.matchedId) setLocationId(data.location.matchedId);
      if (data.lines.length > 0) {
        const rows = data.lines.map((line) => ({
          ...emptyPartRow(),
          partId: line.partId || "",
          quantity: line.quantity ? String(line.quantity) : "",
          deliveryDate: line.deliveryDate || "",
          extracted: line,
        }));
        // Keep any rows the user already filled in by hand
        setParts((prev) => (prev.every(isBlankRow) ? rows : [...prev, ...rows]));
      }
      setExtraction({ ...data, fileName: file.name });
    } catch (err) {
      if (controller.signal.aborted) return;
      setToast({ msg: err.response?.data?.error || "Could not read the PO document", type: "error" });
    } finally {
      if (extractAbortRef.current === controller) {
        extractAbortRef.current = null;
        setExtracting(null);
      }
    }
  };

  const updatePart = (idx, field, value) =>
    setParts((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      // Changing the workflow invalidates any previously chosen stage skips
      if (field === "workflowId") return { ...p, workflowId: value, skippedStageIds: [] };
      return { ...p, [field]: value };
    }));

  const removePart = (idx) => setParts((prev) => prev.filter((_, i) => i !== idx));

  // Convenience for the common case where every part ships on the same day
  const applyFirstDateToAll = () =>
    setParts((prev) => prev.map((p) => ({ ...p, deliveryDate: prev[0].deliveryDate })));

  const checkedParts = validFeasibilityParts
    .filter((p) => feasibility[p._key])
    .map((p) => ({ part: p, feas: feasibility[p._key] }));
  const allFeasible = checkedParts.every(({ feas }) => feas.feasible);

  const v = {
    poNumber: submitted && !poNumber.trim(),
    customerId: submitted && !customerId,
    partId: (idx) => submitted && !parts[idx].partId,
    workflowId: (idx) => submitted && !parts[idx].workflowId,
    quantity: (idx) => submitted && !(Number(parts[idx].quantity) > 0),
    deliveryDate: (idx) => submitted && !parts[idx].deliveryDate,
  };

  const isValid =
    poNumber && customerId &&
    parts.length > 0 &&
    parts.every((p) => p.partId && p.workflowId && Number(p.quantity) > 0 && p.deliveryDate);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const partMasterMap = Object.fromEntries(partsMaster.map((p) => [p.id, p]));
      // PO-level date is the roll-up — the last part to ship
      const poDeliveryDate = parts.map((p) => p.deliveryDate).sort().slice(-1)[0];
      await api.post("/manufacturing/pos", {
        poNumber: poNumber.trim(),
        // The backend resolves the name snapshot from this id
        customerId,
        location: locations.find((l) => l.id === locationId)?.name || null,
        deliveryDate: poDeliveryDate,
        parts: parts.map((p) => ({
          partId: p.partId,
          partType: partMasterMap[p.partId]?.name || p.partId,
          quantity: Number(p.quantity),
          workflowId: p.workflowId,
          deliveryDate: p.deliveryDate,
          skippedStageIds: p.skippedStageIds || [],
        })),
      });
      navigate("/manufacturing/po-management");
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to create PO", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <Link className="back-link" to="/manufacturing/po-management">← Back to PO Management</Link>

      <h1 style={{ margin: "12px 0 4px", color: "#0f2d5c" }}>Create New PO</h1>
      <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 14 }}>
        Configure order details and verify production feasibility.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, padding: 24 }}>

          <POUploadPanel extracting={Boolean(extracting)} extraction={extraction} onFile={handleExtractFile} />

          {/* Top fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>PO Number *</label>
              <input
                style={{ ...inputStyle(v.poNumber), fontFamily: "monospace", fontWeight: 600 }}
                type="text"
                placeholder="e.g. PO-2026-001"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
              />
              {v.poNumber && <p style={errStyle}>PO Number is required</p>}
              {extraction?.poNumber && poNumber === extraction.poNumber && <ExtractHint>Read from PO — please verify</ExtractHint>}
            </div>
            <div>
              <label style={labelStyle}>Customer Name *</label>
              <AddableSelect
                value={customerId}
                onChange={setCustomerId}
                items={customers}
                setItems={setCustomers}
                createItem={async (name) => (await customersAPI.create(name)).data}
                noun="customer"
                placeholder="Search or add customer…"
                hasError={v.customerId}
              />
              {v.customerId && <p style={errStyle}>Customer name is required</p>}
              {extraction?.customer.raw && (
                extraction.customer.matchedId && customerId === extraction.customer.matchedId
                  ? <ExtractHint>Matched “{extraction.customer.raw}” on the PO — please verify</ExtractHint>
                  : !customerId && <ExtractHint warn>PO says “{extraction.customer.raw}” — no matching customer. Select one or add it.</ExtractHint>
              )}
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <AddableSelect
                value={locationId}
                onChange={setLocationId}
                items={locations}
                setItems={setLocations}
                createItem={async (name) => (await poLocationsAPI.create(name)).data.data}
                noun="location"
                placeholder="Search or add location…"
              />
              {extraction?.location.raw && (
                extraction.location.matchedId && locationId === extraction.location.matchedId
                  ? <ExtractHint>Matched from ship-to address — please verify</ExtractHint>
                  : !locationId && <ExtractHint warn>Ship-to on PO: “{extraction.location.raw}”</ExtractHint>
              )}
            </div>
          </div>

          {/* Parts section */}
          <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Parts</h3>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                  Each part has its own delivery date.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {parts.length > 1 && parts[0].deliveryDate && (
                  <button
                    type="button"
                    onClick={applyFirstDateToAll}
                    style={{ background: "none", border: "none", color: "#1d4ed8", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0 }}
                  >
                    Use first date for all parts
                  </button>
                )}
                <button type="button" className="btn-secondary" style={{ fontSize: 13 }} onClick={addPart}>
                  + Add Part
                </button>
              </div>
            </div>
            {partsMaster.length === 0 && (
              <p style={{ fontSize: 12, color: "#f59e0b", margin: "0 0 12px" }}>
                No parts configured. Add parts in Settings → Parts Master first.
              </p>
            )}
            {workflows.length === 0 && (
              <p style={{ fontSize: 12, color: "#f59e0b", margin: "0 0 12px" }}>
                No active workflows found. Activate a workflow in Settings first.
              </p>
            )}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Part</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", width: 200 }}>Workflow</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", width: 180 }}>Stages</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", width: 120 }}>Quantity</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", width: 170 }}>Delivery Date</th>
                  <th style={{ padding: "8px 12px", borderBottom: "1px solid #e4ebf5", width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {parts.map((p, idx) => (
                  <tr key={p._key}>
                    <td style={{ padding: "6px 12px" }}>
                      <PartSelect
                        value={p.partId}
                        onChange={(id) => updatePart(idx, "partId", id)}
                        parts={partsMaster}
                        hasError={v.partId(idx)}
                      />
                      {v.partId(idx) && <p style={errStyle}>Select a part</p>}
                      {p.extracted && (() => {
                        const onPO = [p.extracted.rawPartCode, p.extracted.rawDescription].filter(Boolean).join(" · ");
                        if (!onPO) return null;
                        if (!p.partId) return <ExtractHint warn>PO line: “{onPO}” — not found in Parts Master</ExtractHint>;
                        if (p.partId === p.extracted.partId) return <ExtractHint>Matched PO line “{onPO}”</ExtractHint>;
                        return null;
                      })()}
                      {p.extracted?.warnings.map((w) => <ExtractHint key={w} warn>{w}</ExtractHint>)}
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <select
                        style={inputStyle(v.workflowId(idx))}
                        value={p.workflowId}
                        onChange={(e) => updatePart(idx, "workflowId", e.target.value)}
                      >
                        <option value="">— Select —</option>
                        {workflows.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                      {v.workflowId(idx) && <p style={errStyle}>Select a workflow</p>}
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <StageSkipSelect
                        workflow={workflows.find((w) => w.id === p.workflowId) || null}
                        skippedStageIds={p.skippedStageIds || []}
                        onChange={(ids) => updatePart(idx, "skippedStageIds", ids)}
                      />
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <input
                        style={inputStyle(v.quantity(idx))}
                        type="number"
                        min="1"
                        placeholder="e.g. 500"
                        value={p.quantity}
                        onChange={(e) => updatePart(idx, "quantity", e.target.value)}
                      />
                      {v.quantity(idx) && <p style={errStyle}>Enter a quantity</p>}
                    </td>
                    <td style={{ padding: "6px 12px" }}>
                      <input
                        style={inputStyle(v.deliveryDate(idx))}
                        type="date"
                        min={today}
                        value={p.deliveryDate}
                        onChange={(e) => updatePart(idx, "deliveryDate", e.target.value)}
                      />
                      {v.deliveryDate(idx) && <p style={errStyle}>Pick a delivery date</p>}
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center" }}>
                      {parts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePart(idx)}
                          style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700, fontSize: 18 }}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalQty > 0 && (
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#64748b" }}>
                Total quantity: <strong>{totalQty.toLocaleString()}</strong> units
              </p>
            )}
          </div>

          {/* Feasibility check — one row per part, each against its own delivery date */}
          {validFeasibilityParts.length > 0 && (
            <div style={{
              marginTop: 20, borderTop: "1px solid #f1f5f9",
              background: "#eff6ff", borderRadius: 10, padding: 16,
            }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "#1d4ed8" }}>
                Feasibility Check
              </h3>
              {feasibilityLoading && (
                <p style={{ color: "#64748b", fontSize: 13 }}>Calculating...</p>
              )}
              {!feasibilityLoading && checkedParts.length > 0 && (
                <div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Part", "Delivery Date", "Lead Time", "Est. Completion", "Buffer", "Starts", ""].map((h) => (
                          <th key={h} style={{
                            padding: "6px 10px", textAlign: "left", fontSize: 11, fontWeight: 700,
                            color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px",
                            borderBottom: "1px solid #dbeafe", whiteSpace: "nowrap",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {checkedParts.map(({ part, feas }) => (
                        <tr key={part._key} style={{ borderBottom: "1px solid #dbeafe" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 600, color: "#0f2d5c" }}>
                            {partsMaster.find((pm) => pm.id === part.partId)?.name || "—"}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#0f2d5c" }}>
                            {new Date(part.deliveryDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#0f2d5c" }}>{feas.totalDaysRequired} days</td>
                          <td style={{ padding: "8px 10px", color: "#0f2d5c" }}>
                            {new Date(feas.estimatedCompletionDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: "8px 10px", fontWeight: 700, color: feas.bufferDays >= 0 ? "#15803d" : "#dc2626" }}>
                            {feas.bufferDays} days
                          </td>
                          <td style={{ padding: "8px 10px", color: "#64748b" }}>
                            {feas.startImmediately ? "Immediately" : new Date(feas.scheduledStartDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>
                            <span style={{
                              fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                              background: feas.feasible ? "#dcfce7" : "#fee2e2",
                              color: feas.feasible ? "#15803d" : "#dc2626",
                              border: `1px solid ${feas.feasible ? "#86efac" : "#fca5a5"}`,
                              borderRadius: 999, padding: "3px 12px",
                            }}>
                              {feas.feasible ? "✓ Feasible" : "✗ Not Feasible"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ textAlign: "center", marginTop: 14 }}>
                    <span style={{
                      fontSize: 15, fontWeight: 800,
                      background: allFeasible ? "#dcfce7" : "#fee2e2",
                      color: allFeasible ? "#15803d" : "#dc2626",
                      border: `2px solid ${allFeasible ? "#86efac" : "#fca5a5"}`,
                      borderRadius: 999, padding: "6px 20px",
                    }}>
                      {allFeasible ? "✓ All parts feasible" : "✗ Some parts not feasible"}
                    </span>
                  </div>
                  {!allFeasible && (
                    <p style={{ margin: "12px 0 0", fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px" }}>
                      The parts marked above cannot be completed by their delivery dates based on current
                      benchmarks. You may still proceed — a warning will be recorded.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Link to="/manufacturing/po-management" className="btn-secondary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Cancel
            </Link>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create PO"}
            </button>
          </div>
        </div>
      </form>

      {extracting && (
        <POExtractOverlay
          fileName={extracting}
          onCancel={() => {
            extractAbortRef.current?.abort();
            extractAbortRef.current = null;
            setExtracting(null);
          }}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
